import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { DropDown, Input } from "../../Common";
import DataTable from "../../Common/DataTable/DataTable";
import Button from "../../Common/Button";
import DatePicker from "../../Common/DatePicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./EventManagement.module.css";
import { createNewEvent, fetchNewEvent } from "../../api";
import { EventColumns } from "../../utils/tableHeader";
import withFeatureActions from "../../withFeatureActions";
import singleBackArrow from "../../public/images/singleBackArrow.svg";
import { Spinner } from "../../Common";

const EventManagement = ({ featureAccess }) => {
  const [form, setForm] = useState({
    applicationName: "",
    startTime: "",
    endTime: "",
    message: "",
  });
  const { writeAccess } = featureAccess;
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [eventList, setEventList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);

  const appList = [
    { label: "MCA", value: "MCA" },
    { label: "INVESTING", value: "INVESTING" },
    { label: "EASYREMIT", value: "EASYREMIT" },
  ];

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    let err = {};
    const now = new Date();

    if (!form.applicationName) err.applicationName = "Application is required";
    if (!form.startTime) err.startTime = "Start Time is required";
    if (!form.endTime) err.endTime = "End Time is required";
    if (!form.message.trim()) err.message = "Message is required";

    if (form.startTime) {
      const start = new Date(form.startTime);
      if (start < now) err.startTime = "Start time cannot be before now";
    }

    if (form.endTime) {
      const end = new Date(form.endTime);
      if (end < now) err.endTime = "End time cannot be before now";

      if (form.startTime) {
        const start = new Date(form.startTime);
        if (end <= start) err.endTime = "End time must be after start time";
      }
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const getEventTable = async () => {
    setTableLoading(true);
    try {
      const resp = await fetchNewEvent();
    if (resp?.data?.code === 200) {
      setEventList(resp.data.data || []);
      } else {
        setEventList([]);
      }
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    getEventTable();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (!validate()) return;
    const payload = {
      applicationName: form.applicationName,
      startTime: form.startTime,
      endTime: form.endTime,
      message: form.message,
    };

    try {
      setLoading(true);
      const resp = await createNewEvent(payload);

      if (resp?.data?.code === 200) {
        setMsg({ type: "success", text: "Event created successfully!" });
        setTimeout(() => {
          setMsg({ type: "", text: "" });
          setShowForm(false);
          getEventTable();
        }, 3000);
        setForm({
          applicationName: "",
          startTime: "",
          endTime: "",
          message: "",
        });
      } else {
        setMsg({
          type: "error",
          text: resp?.data?.message || "Failed to create event.",
        });
      }
    } catch (error) {
      setMsg({ type: "error", text: "Something went wrong" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {!showForm && (
        <div className={styles.tableBox}>
        {tableLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
            <Spinner />
          </div>
        ) : (
          <DataTable
            columns={EventColumns}
            list={eventList}
            pagination={false}
            title="Event List"
            titleButton={
              writeAccess && (
                <Button
                  title="Add New Event"
                  onClick={() => setShowForm(true)}
                />
              )
            }
          />
        )}
        </div>
      )}
      {showForm && (
        <div className={styles.createRoleCard}>
          <div className={styles.backButtons}>
            <img
              alt="Back"
              src={singleBackArrow}
              onClick={() => setShowForm(false)}
              className={styles.arrowButton}
            />
            <span
              style={{
                fontSize: "13px",
                marginLeft: "5px",
                cursor: "pointer",
                color: "white",
              }}
              onClick={() => setShowForm(false)}
            >
              Back
            </span>
          </div>

          <div className={styles.card}>
            <form onSubmit={handleSubmit}>
              <h3 style={{ color: "white" }}>Create New Event</h3>

              <Row className="my-3">
                <Col md={12}>
                  <DropDown
                    label="Application Name"
                    value={form.applicationName}
                    data={appList}
                    onChange={(val) => handleChange("applicationName", val)}
                    placeholder="Select Application"
                    required
                    error={!!errors.applicationName}
                    helperText={errors.applicationName}
                  />
                </Col>
              </Row>

              <div className={styles.fieldstyle}>
                <Row className="my-3">
                  <label className={styles.label}>
                    Start Time <span className={styles.required}>*</span>
                  </label>

                  <DatePicker
                    selected={form.startTime ? new Date(form.startTime) : null}
                    onChange={(date) =>
                      handleChange(
                        "startTime",
                        date.toLocaleString("sv-SE").replace("T", " ")
                      )
                    }
                    showTime={true}
                    dateFormat="yyyy-MM-dd HH:mm:ss"
                    placeholderText="Select Start Time"
                    minDate={new Date()}
                    className={styles.datePicker}
                  />

                  {errors.startTime && (
                    <div className={styles.error}>{errors.startTime}</div>
                  )}
                </Row>
              </div>
              <div className={styles.fieldstyle}>
                <Row className="my-3">
                  <label className={styles.label}>
                    End Time <span className={styles.required}>*</span>
                  </label>
                  <DatePicker
                    selected={form.endTime ? new Date(form.endTime) : null}
                    onChange={(date) =>
                      handleChange(
                        "endTime",
                        date.toLocaleString("sv-SE").replace("T", " ")
                      )
                    }
                    showTime={true}
                    dateFormat="yyyy-MM-dd HH:mm:ss"
                    placeholderText="Select End Time"
                    minDate={new Date()}
                    className={styles.datePicker}
                  />
                  {errors.endTime && (
                    <div className={styles.error}>{errors.endTime}</div>
                  )}
                </Row>
              </div>

              <Row className="my-3">
                <Col md={12}>
                  <Input
                    label="Message"
                    placeholder="Enter Message"
                    name="message"
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    isTextArea
                    required
                    inputClassName={styles.commentBox}
                    error={!!errors.message}
                    helperText={errors.message}
                  />
                </Col>
              </Row>

              {msg.text && (
                <p
                  style={{
                    color: msg.type === "success" ? "green" : "red",
                    textAlign: "center",
                  }}
                >
                  {msg.text}
                </p>
              )}

              <Row className="my-3">
                <div className={styles.submitButton}>
                  <Button
                    onClick={handleSubmit}
                    title="Submit"
                    loading={loading}
                    disabled={loading}
                    width="100%"
                  />
                </div>
              </Row>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default withFeatureActions(EventManagement, "ADMIN", "Event Management");
